import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  EmptyState,
  EmptyStateTitle,
  PageLoader,
  Paragraph,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import { buildLogRows, buildRelationLookups } from '@/services/db/activity-log/queries';
import { getObjectLogsQueryOptions, type ObjectLogsParams } from '@/services/db/object/queries';
import type { ActivityLogRow } from '@/services/db/activity-log/entity';

/**
 * An object's activity logs, with the sideloaded people/objects/segments resolved
 * onto each row. `segmentById` is the lookup an expanded row needs to turn segment
 * ids in a log payload into links.
 */
export function useObjectActivityLogs(workspaceId: number, cioObjectId: string, params: ObjectLogsParams) {
  const query = useQuery(getObjectLogsQueryOptions(workspaceId, cioObjectId, params));
  const pages = useMemo(() => (query.data != null && 'logs' in query.data ? [query.data] : []), [query.data]);
  const lookups = useMemo(() => buildRelationLookups(pages), [pages]);
  const rows = useMemo(() => buildLogRows(pages, lookups), [pages, lookups]);

  return { query, rows, segmentById: lookups.segmentById };
}

export type ActivityLogTableColumn = { label: string; textAlign?: 'left' | 'right' };

/** The query state the table branches on, so callers needn't pass a whole query result. */
export type ActivityLogTableQuery = {
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
};

type ActivityLogTableProps = {
  caption: string;
  columns: readonly ActivityLogTableColumn[];
  query: ActivityLogTableQuery;
  /** Shown in place of the rows when the query failed, above a Try again button. */
  errorMessage: string;
  rows: readonly ActivityLogRow[];
  shouldHighlightOnHover?: boolean;
  renderRow: (row: ActivityLogRow) => ReactNode;
};

type StateRowProps = { colSpan: number; withPadding?: boolean; children: ReactNode };

function StateRow({ colSpan, withPadding = false, children }: StateRowProps) {
  return (
    <TableTr>
      <TableTd colSpan={colSpan} px={withPadding ? '300' : undefined}>
        {children}
      </TableTd>
    </TableTr>
  );
}

/**
 * The activity-log table an object detail tab renders: the shared header row and
 * the loading, error, empty, and populated states. Callers own the surrounding
 * panel and each row's cells.
 */
export function ActivityLogTable(props: ActivityLogTableProps) {
  const { query, columns, rows } = props;
  const colSpan = columns.length;

  return (
    <Table caption={props.caption} layout="fixed" mt="200" shouldHighlightOnHover={props.shouldHighlightOnHover}>
      <TableThead>
        <TableTr>
          {columns.map((column) => (
            <TableTh key={column.label} textAlign={column.textAlign ?? 'left'}>
              {column.label}
            </TableTh>
          ))}
        </TableTr>
      </TableThead>
      <TableTbody>
        {query.isPending ? (
          <StateRow colSpan={colSpan} withPadding>
            <PageLoader />
          </StateRow>
        ) : query.isError ? (
          <StateRow colSpan={colSpan} withPadding>
            <Paragraph variant="secondary">{props.errorMessage}</Paragraph>
            <Paragraph textAlign="center">
              <Button
                isLoading={query.isFetching}
                variant="secondary"
                size="sm"
                onClick={() => query.refetch()}
                icon="reset"
                iconPosition="leading"
              >
                Try again
              </Button>
            </Paragraph>
          </StateRow>
        ) : rows.length === 0 ? (
          <StateRow colSpan={colSpan}>
            <EmptyState>
              <EmptyStateTitle icon="activity">No recent activity</EmptyStateTitle>
            </EmptyState>
          </StateRow>
        ) : (
          rows.map((row) => props.renderRow(row))
        )}
      </TableTbody>
    </Table>
  );
}
